import {
  BdcBillingCreateRequest,
  BdcBillingDetail,
  BdcBillingListData,
  BdcBillingListParams,
  BdcBillingPreview,
} from "../types/bdc.type";
import { ApiResponse } from "../types/express-data.type";
import axios from "./axios";

/**
 * BDC · ใบวางบิล (P2) — `/bdc/billings`, head-office accounting + MASTER (every branch).
 *
 *   GET  /billings/preview      date_from, date_to → totals, rows (≤ 2000), checks, suggested no. / dates
 *   POST /billings              { date_from, date_to, doc_date, due_date } — refused while a check is an error
 *   GET  /billings              page, limit, year?, status?
 *   GET  /billings/:uuid        head + rows
 *   POST /billings/:uuid/cancel { reason } — frees its INVs / CNs for another billing
 *   GET  /billings/:uuid/xlsx | /pdf | /package (zip: IFS xlsx + pdf + the POs' ORDS / PICKLST)
 */

const billingPath = (uuid: string) => `/bdc/billings/${encodeURIComponent(uuid)}`;
const ownErrors = { suppressForbiddenRedirect: true, suppressErrorToast: true } as const;

export const previewBdcBilling = (dateFrom: string, dateTo: string) =>
  axios.get<ApiResponse<BdcBillingPreview>>(
    `/bdc/billings/preview?${new URLSearchParams({ date_from: dateFrom, date_to: dateTo }).toString()}`,
    { timeout: 120000, meta: ownErrors },
  );

export const createBdcBilling = (body: BdcBillingCreateRequest) =>
  axios.post<ApiResponse<BdcBillingDetail>>(`/bdc/billings`, body, { timeout: 120000, meta: ownErrors });

export const listBdcBillings = (params: BdcBillingListParams) => {
  const query = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  if (params.year) query.append("year", String(params.year));
  if (params.status) query.append("status", params.status);
  return axios.get<ApiResponse<BdcBillingListData>>(`/bdc/billings?${query.toString()}`);
};

export const getBdcBilling = (uuid: string) => axios.get<ApiResponse<BdcBillingDetail>>(billingPath(uuid));

export const cancelBdcBilling = (uuid: string, reason: string) =>
  axios.post<ApiResponse<BdcBillingDetail>>(
    `${billingPath(uuid)}/cancel`,
    { reason },
    { meta: { successMsg: "ยกเลิกใบวางบิลแล้ว", suppressForbiddenRedirect: true } },
  );

export type BdcBillingFile = "xlsx" | "pdf" | "package";

/** One file of a billing as a Blob (the package is a zip of xlsx + pdf + ORDS / PICKLST). */
export const downloadBdcBilling = (uuid: string, file: BdcBillingFile) =>
  axios.get<Blob>(`${billingPath(uuid)}/${file}`, {
    responseType: "blob",
    timeout: 180000,
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        404: "ไม่พบใบวางบิลนี้",
        502: "ระบบสร้างเอกสารตอบช้าเกินไป กรุณาลองใหม่อีกครั้ง",
        503: "ระบบสร้างเอกสารไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });
